import React from "react";
import {
  Box,
  Chip,
  Paper,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";

/**
 * La etiqueta de calidad de un importe. Hoy todo es orientativo: la valoración de la
 * Proposal Factory todavía no existe. Gris con borde discontinuo, como en la
 * especificación.
 */
export const QualityChip: React.FC = () => (
  <Tooltip title="Presupuesto orientativo: aún no hay valoración de la Proposal Factory">
    <Chip
      size="small"
      variant="outlined"
      label="Orientativo"
      sx={{ height: 20, fontSize: 10, borderStyle: "dashed" }}
    />
  </Tooltip>
);

interface SectionProps {
  title: string;
  subtitle?: string;
  /** Algo a la derecha del título: un selector, la etiqueta de calidad... */
  action?: React.ReactNode;
  children: React.ReactNode;
}

/** Un bloque del dashboard: tarjeta con título. */
export const Section: React.FC<SectionProps> = ({
  title,
  subtitle,
  action,
  children,
}) => {
  const theme = useTheme();
  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 3,
        border: `1px solid ${theme.palette.grey[200]}`,
        p: 2,
        minWidth: 0,
      }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
        gap={1}
        mb={1.5}
      >
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="caption" color="text.secondary">
              {subtitle}
            </Typography>
          )}
        </Box>
        {action}
      </Stack>
      {children}
    </Paper>
  );
};

interface BarRowProps {
  label: string;
  /** 0-1: el ancho de la barra. */
  share: number;
  value: string;
  caption?: string;
  onClick?: () => void;
  color?: string;
}

/** Una fila con barra horizontal. Para funnels, repartos y tramos. */
export const BarRow: React.FC<BarRowProps> = ({
  label,
  share,
  value,
  caption,
  onClick,
  color,
}) => {
  const theme = useTheme();
  return (
    <Box
      onClick={onClick}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={(event) => {
        if (onClick && (event.key === "Enter" || event.key === " ")) {
          event.preventDefault();
          onClick();
        }
      }}
      sx={{
        cursor: onClick ? "pointer" : "default",
        borderRadius: 1,
        px: 0.5,
        py: 0.5,
        "&:hover": onClick
          ? { backgroundColor: theme.palette.action.hover }
          : undefined,
      }}
    >
      <Stack direction="row" justifyContent="space-between" gap={1}>
        <Typography variant="body2">{label}</Typography>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {value}
        </Typography>
      </Stack>
      <Box
        sx={{
          mt: 0.5,
          height: 8,
          borderRadius: 4,
          backgroundColor: theme.palette.grey[200],
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            width: `${Math.max(0, Math.min(1, share)) * 100}%`,
            height: "100%",
            backgroundColor: color ?? theme.palette.primary.main,
          }}
        />
      </Box>
      {caption && (
        <Typography variant="caption" color="text.secondary">
          {caption}
        </Typography>
      )}
    </Box>
  );
};

/** Texto cuando un bloque no tiene datos todavía. */
export const Empty: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
    {children}
  </Typography>
);

/** Rejilla de bloques: una columna en móvil, dos en pantallas anchas. */
export const BlockGrid: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <Box
    sx={{
      display: "grid",
      gap: 2,
      gridTemplateColumns: { xs: "1fr", lg: "repeat(2, minmax(0, 1fr))" },
    }}
  >
    {children}
  </Box>
);

/** Rejilla de KPIs. */
export const KpiGrid: React.FC<{
  columns?: number;
  children: React.ReactNode;
}> = ({ columns = 5, children }) => (
  <Box
    sx={{
      display: "grid",
      gap: 2,
      gridTemplateColumns: {
        xs: "1fr",
        sm: "repeat(2, minmax(0, 1fr))",
        lg: `repeat(${columns}, minmax(0, 1fr))`,
      },
    }}
  >
    {children}
  </Box>
);
